import axios, { AxiosError } from 'axios';
import { IState } from 'country-state-city';

import { config } from '../config';
import {
  ICcFeatureFlag,
  IConfirmPaypalOrderPayload,
  ICreateKlarnaCustomerTokenPayload,
  IEverflowAddToCartEvent,
  IGetKlarnaPaymentSessionPayload,
  IImportClickPayload,
  IImportLeadPayload,
  IImportLeadResponse,
  IImportOneClickPayload,
  IImportOrderApplePayExpressPayload,
  IImportOrderApplePayPayload,
  IImportOrderCreditCardPayload,
  IImportOrderGooglePayPayload,
  IImportOrderKlarnaPayload,
  IImportOrderKlarnStripePayload,
  IImportOrderPaypalPayload,
  IImportOrderResponse,
  IImportUpsaleKlarnStripePayload,
  IPurchaseApplePayUpsellPayload,
  IPurchaseCreditCardUpsellPayload,
  IPurchaseGooglePayUpsellPayload,
  IPurchasePayPalUpsellPayload,
  IQueryOrderResponse,
  IStartedCheckoutEvent,
  IUpdateCustomerPayload,
  IUpdateOrderPayload,
  IZipValidateResponse,
} from '../types/api';
import { backoff } from '../utils/api';
import { createUrl } from '../utils/url';
import { getAppUtms } from '../utils/utms';

const instance = axios.create({
  baseURL: config.API_URL,
  headers: {
    Authorization: `Basic ${config.TOKEN}`,
  },
});

instance.interceptors.response.use(
  response => {
    response.data = response.data.data.result;
    return response;
  },
  (error: AxiosError) => {
    const errorInfo = {
      reqData: error.response && error.response.data,
      reqStatus: error.response && error.response.status,
      reqHeaders: error.response && error.response.headers,
      reqConfig: error.config,
    };
    Errors.report(error, errorInfo, true);
    return Promise.reject(error);
  },
);

export interface IMakeError {
  url?: string;
  email?: string;
  message: string;
  orderId?: string;
  sentryEventId?: string;
  error_message?: string;
}

interface IMakePaymentButtonTracking {
  ppId?: number;
  orderId: string;
  source: string;
}

interface IAffIdData {
  url: string;
  brand: string;
  email: string;
  orderId: string;
  sessionId: string;
}

interface GtmEventData {
  event: string;
  data: any;
}

export class Api {
  static reportAffId = async (data: IAffIdData) => {
    try {
      const res = await axios.post(
        'https://trackaffidv2-f2ow6o2dfq-uc.a.run.app',
        data,
      );
      return res.data;
    } catch (e) {
      Errors.report(
        e,
        {
          data,
        },
        false,
        false,
      );
    }
  };

  static sendImportantErrorToMake = async (e: IMakeError) => {
    const metaCountry = document.querySelector(
      'meta[name="cf-country-code"]',
    ) as HTMLMetaElement;
    const country = metaCountry ? metaCountry.content : 'unknown';
    const makeError: Record<string, string> = {
      country,
      version: config.RELEASE,
      url: window.location.href,
      sentryEventId: e.sentryEventId || 'N/A',
      message: window.DEBUG ? `TEST: ${e.message}` : e.message,
      error_message: window.DEBUG ? `TEST: ${e.message}` : e.message,
    };
    if (e?.email) makeError.email = e.email;
    if (e?.orderId) makeError.orderId = e.orderId;
    const url = createUrl(config.MAKE_WH_URL, makeError);
    await axios.get(url);
  };

  static sendPaymentTypeToMake = async (props: IMakePaymentButtonTracking) => {
    try {
      const url = createUrl(config.MAKE_PP_URL, {
        source: props.source,
        order: props.orderId,
        url: window.location.href,
        paypalBillerId: String(props.ppId),
      });
      axios.get(url);
    } catch (e) {
      Errors.report(
        e,
        {
          props,
        },
        false,
        false,
      );
    }
  };

  static getGwConfig = async (currency?: string, country?: string) => {
    try {
      if (!currency || currency !== 'USD') return undefined;
      const href = document.location.href;
      const url = `${config.MAKE_PAYMENT_BALANCER_URL}?country=${country}&url=${href}`;
      const res = await axios.get(url);
      if (res.data === 'Accepted') {
        return undefined;
      }
      return res.data as ICcFeatureFlag;
    } catch (e) {
      Errors.report(e, undefined, undefined, false);
    }
  };

  static fetchStates = async (country: string): Promise<IState[]> => {
    const url = `${config.API_URL}/states`;
    return backoff(async () => {
      const res = await axios.post(url, { country });
      return res.data;
    });
  };

  static validateZipCode = async (zip: string) => {
    const url = `${config.API_URL}/validate-zip`;
    const res = await axios.post(url, { zip });
    return res.data as IZipValidateResponse;
  };

  static fetchOneClickProduct = async (pid: string) => {
    const res = await instance.post('konnektive/query-product', {
      client: window.BRAND,
      productId: pid,
    });
    return res.data[0];
  };

  static importClick = async (data: IImportClickPayload): Promise<string> => {
    return backoff(async () => {
      const res = await instance.post('konnektive/import-click', data);
      return res.data.sessionId;
    });
  };

  static importLead = async (data: IImportLeadPayload) => {
    const res = await instance.post('konnektive/import-lead', data);
    return res.data as IImportLeadResponse;
  };

  static importOneClick = async (data: IImportOneClickPayload) => {
    const res = await instance.post('konnektive/one-click-order', data);
    return res.data as IImportOrderResponse;
  };

  static importPaypalOrder = async (data: IImportOrderPaypalPayload) => {
    const res = await instance.post('konnektive/paypal/import-order', data);
    return res.data as IImportOrderResponse;
  };

  static confirmPaypalPayment = async (data: IConfirmPaypalOrderPayload) => {
    const res = await instance.post('konnektive/paypal/confirm', data);
    return res.data;
  };

  static importCreditCardOrder = async (
    data: IImportOrderCreditCardPayload,
  ) => {
    const res = await instance.post('konnektive/creditcard/import-order', data);
    return res.data as IImportOrderResponse;
  };

  static importPaypalUpsell = async (data: IPurchasePayPalUpsellPayload) => {
    const res = await instance.post('konnektive/paypal/import-upsale', data);
    return res.data;
  };

  static importApplePayOrder = async (data: IImportOrderApplePayPayload) => {
    const res = await instance.post('konnektive/applePay/import-order', data);
    return res.data;
  };

  static importApplePayOrderExpress = async (
    data: IImportOrderApplePayExpressPayload,
  ) => {
    const res = await instance.post(
      'konnektive/applePay/import-order-express',
      data,
    );
    return res.data;
  };

  static importApplePayUpsell = async (
    data: IPurchaseApplePayUpsellPayload,
  ) => {
    const res = await instance.post('konnektive/applePay/import-upsale', data);
    return res.data;
  };

  static importGooglePayOrder = async (data: IImportOrderGooglePayPayload) => {
    const res = await instance.post('konnektive/googlePay/import-order', data);
    return res.data;
  };

  static importGooglePayUpsell = async (
    data: IPurchaseGooglePayUpsellPayload,
  ) => {
    const res = await instance.post('konnektive/googlePay/import-upsale', data);
    return res.data;
  };

  static importCreditCardUpsell = async (
    data: IPurchaseCreditCardUpsellPayload,
  ) => {
    const res = await instance.post(
      'konnektive/creditcard/import-upsale',
      data,
    );
    return res.data;
  };

  static queryOrder = async (orderId: string) => {
    try {
      const res = await instance.post('konnektive/query-order', {
        orderId,
      });
      return res.data as IQueryOrderResponse;
    } catch (e) {
      Errors.report(
        e,
        {
          orderId,
          geo: window.GEO,
        },
        false,
        false,
      );
      return null;
    }
  };

  static updateOrder = async (data: IUpdateOrderPayload) => {
    const res = await instance.post('konnektive/update-order', data);
    return res.data;
  };

  static updateCustomer = async (data: IUpdateCustomerPayload) => {
    try {
      const res = await instance.post('konnektive/update-customer', data);
      return res.data;
    } catch (e) {
      Errors.report(
        e,
        {
          geo: window.GEO,
          customer: data,
        },
        false,
        false,
      );
    }
  };

  // KLARNA

  static getKlarnaPaymentSession = async (
    payload: IGetKlarnaPaymentSessionPayload,
  ) => {
    const res = await instance.post('klarna/get-payment-session', payload);
    return res.data;
  };

  static getCustomerToken = async (data: IGetKlarnaPaymentSessionPayload) => {
    const res = await instance.post('klarna/get-customer-token', data);
    return res.data;
  };

  static createKlarnaPaymentsOrder = async (
    data: IGetKlarnaPaymentSessionPayload,
  ) => {
    const res = await instance.post('klarna/create-payments-order', data);
    return res.data;
  };

  static importKlarnaStripeOrder = async (
    data: IImportOrderKlarnStripePayload,
  ) => {
    const res = await instance.post(
      'konnektive/klarna/import-order-stripe',
      data,
    );
    return res.data;
  };

  static importKlarnaStripeUpsell = async (
    data: IImportUpsaleKlarnStripePayload,
  ) => {
    const res = await instance.post(
      'konnektive/klarna/import-upsale-stripe',
      data,
    );

    return res.data;
  };

  static importKlarnaOrder = async (data: IImportOrderKlarnaPayload) => {
    const res = await instance.post('konnektive/klarna/import-order', data);
    return res.data.result;
  };

  static createKlarnaCustomerTokenOrder = async (
    data: ICreateKlarnaCustomerTokenPayload,
  ) => {
    const res = await instance.post('klarna/create-customer-token-order', data);
    return res.data;
  };

  //Everflow
  static sendEveflowAddToCart = async (data: IEverflowAddToCartEvent) => {
    const res = await instance.post('everflow/track-add-to-cart', data);
    return res.data;
  };

  // Klaviyo
  static sendStartedCheckoutEventV2 = async (data: IStartedCheckoutEvent) => {
    const res = await instance.post('klaviyo/started-checkout-v2', data);
    return res.data;
  };

  static sendStartedCheckoutEvent = async (data: IStartedCheckoutEvent) => {
    const res = await instance.post('klaviyo/started-checkout', data);
    return res.data;
  };

  // events
  static sendGtmDataToBigQuery = async (data: GtmEventData) => {
    try {
      const res = await instance.post('bq/events/gtm', {
        url: window.location.href,
        auid: getAppUtms()?.custom_order_auid || 'AUID_NOT_FOUND',
        orderId: data?.data?.transaction_id,
        email: data?.data?.user_id,
        client: data?.data?.brand,
        version: window['main'].getAttribute('data-version'),
        debug: window.DEBUG || false,
        sessionId: data?.data?.sessionId,
        customerId: data?.data?.user_id,
        ...data,
      });
      return res.data;
    } catch (e) {
      console.log(e);
      // INFINITE LOOP ALERT
      // Errors.report(
      //   e,
      //   {
      //     data,
      //   },
      //   false,
      //   false,
      // );
    }
  };

  static getMerchants = async (data: any) => {
    const res = await instance.post('konnektive/query-merchant', data);
    return res.data;
  };
}
